import { useState, useEffect, useMemo, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Upload, FileText, CheckCircle, AlertCircle, Shield, FileCheck, ArrowLeft, ArrowRight, RefreshCw, Loader, Clock, UserCheck } from 'lucide-react'
import toast from 'react-hot-toast'
import AuthHeader from '../../components/auth/AuthHeader'
import RegistrationSidebar from '../../components/registration/RegistrationSidebar'
import StepIndicator from '../../components/registration/StepIndicator'
import { vendorService } from '../../services/vendorService'
import { useAuth } from '../../context/AuthContext'
import { saveToLocalStorage, getFromLocalStorage, removeFromLocalStorage } from '../../utils/localStorage'

const DocumentUploadPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const [documents, setDocuments] = useState([])
  const [uploading, setUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState({})
  const [isLoading, setIsLoading] = useState(true)
  const [isCheckingStatus, setIsCheckingStatus] = useState(true)
  const [adminReviewStatus, setAdminReviewStatus] = useState(null)
  const [isStepCompleted, setIsStepCompleted] = useState(false)
  const [hasSubmittedForReview, setHasSubmittedForReview] = useState(false)
  const [verificationStatuses, setVerificationStatuses] = useState({
    documents: 'pending',
    bgv: 'pending',
    adminReview: 'pending',
    approval: 'pending'
  })

  // Ref to prevent multiple simultaneous API calls
  const isCheckingRef = useRef(false)
  const hasNavigatedRef = useRef(false)

  // Required documents for vendor
  const requiredDocuments = [
    {
      type: 'certificate_of_incorporation',
      label: 'Certificate of Incorporation',
      description: 'Upload your certificate of incorporation (PDF, JPG, PNG)',
      required: true,
      maxSize: 5
    },
    {
      type: 'gst_registration_certificate',
      label: 'GST Registration Certificate',
      description: 'Upload your GST registration certificate',
      required: true,
      maxSize: 5
    },
    {
      type: 'pan_card_copy',
      label: 'PAN Card Copy',
      description: 'Upload company PAN card',
      required: true,
      maxSize: 2
    },
    {
      type: 'audited_financial_statements_(last_2_years)',
      label: 'Audited Financial Statements (Last 2 Years)',
      description: 'Upload audited financial statements',
      required: true,
      maxSize: 5
    },
    {
      type: 'signed_nda',
      label: 'Signed NDA',
      description: 'Upload signed NDA document',
      required: true,
      maxSize: 5
    },
    {
      type: 'master_service_agreement',
      label: 'Master Service Agreement',
      description: 'Upload master service agreement',
      required: true,
      maxSize: 5
    }
  ]

  // Check registration status and load existing documents
  useEffect(() => {
    const checkStatus = async () => {
      // Prevent multiple simultaneous calls
      if (isCheckingRef.current || hasNavigatedRef.current) {
        return
      }

      // Only run if we're on the documents page
      if (location.pathname !== '/vendor/registration/documents') {
        return
      }

      try {
        isCheckingRef.current = true
        setIsCheckingStatus(true)
        const currentUser = await vendorService.getCurrentUser()
        
        if (!currentUser) {
          hasNavigatedRef.current = true
          toast.error('Please log in to continue')
          navigate('/auth/login')
          return
        }

        // Check if previous steps completed
        const vendorId = currentUser.vendor_id || currentUser.vendorId || sessionStorage.getItem('vendorId')
        if (!vendorId) {
          hasNavigatedRef.current = true
          toast.error('Please complete company details first')
          navigate('/vendor/registration/legal-details', { replace: true })
          return
        }

        // Check if subscription completed - but don't redirect if we're already here
        const hasSubscription = currentUser.vendor_subscription || currentUser.subscription
        const regStatus = currentUser.hasCompletedRegistration
        
        // Only redirect if subscription is missing AND user hasn't completed registration
        // If hasCompletedRegistration is true, they should be on documents page
        if (!hasSubscription && regStatus !== true && regStatus !== 'true' && regStatus !== 1 && regStatus !== '1') {
          hasNavigatedRef.current = true
          toast.error('Please complete subscription first')
          navigate('/vendor/registration/subscription', { replace: true })
          return
        }

        // Initialize documents
        let docs = requiredDocuments.map(doc => ({
          ...doc,
          id: doc.type,
          status: 'pending',
          file: null,
          preview: null,
          uploadedAt: null,
          fileName: null,
          fileSize: null,
          documentId: null,
          fileUrl: null,
          rejectionReason: null
        }))

        // Load existing documents from API response
        if (currentUser.all_documents_urls) {
          docs = docs.map(doc => {
            const docKey = doc.type
            const existingDoc = currentUser.all_documents_urls[docKey]
            
            if (existingDoc) {
              // Map API status to our status
              let mappedStatus = 'pending'
              if (existingDoc.status === 'APPROVED' || existingDoc.status === 'approved') {
                mappedStatus = 'uploaded'
              } else if (existingDoc.status === 'REJECTED' || existingDoc.status === 'rejected') {
                mappedStatus = 'rejected'
              } else if (existingDoc.status === 'PENDING' || existingDoc.status === 'pending') {
                mappedStatus = 'uploaded' // Show as uploaded if pending review
              } else if (existingDoc.status === 'GENERATED') {
                mappedStatus = 'uploaded' // Generated documents are considered uploaded
              }

              return {
                ...doc,
                status: mappedStatus,
                documentId: existingDoc.document_id || null,
                fileUrl: existingDoc.file_url || null,
                fileName: existingDoc.file_name || existingDoc.fileName || doc.label,
                fileSize: existingDoc.size ? `${(existingDoc.size / (1024 * 1024)).toFixed(2)} MB` : null,
                uploadedAt: existingDoc.uploadDate || existingDoc.upload_date || null,
                rejectionReason: existingDoc.reason || existingDoc.rejection_reason || null,
                preview: existingDoc.file_url || null // Use file_url as preview for display
              }
            }
            return doc
          })
        }

        setDocuments(docs)

        // Check admin review status and BGV status
        const adminReview = currentUser.admin_review || currentUser.adminReview
        const bgvStatus = currentUser.BGV_status || currentUser.bgv_status || 'pending'
        setAdminReviewStatus(adminReview)

        // Check if documents are uploaded
        const hasDocuments = currentUser.all_documents_urls && Object.keys(currentUser.all_documents_urls).length > 0
        
        // Check if any document is rejected
        const hasRejectedDocuments = docs.some(doc => doc.status === 'rejected')
        
        // Mark step as completed if documents are uploaded (but not if rejected)
        if (hasDocuments && !hasRejectedDocuments) {
          setIsStepCompleted(true)
        }

        // Check if BGV status is already uploaded/completed (user has already submitted)
        // But don't set if there are rejected documents - user needs to re-upload
        const bgvUploadedOrCompleted = bgvStatus === 'uploaded' || bgvStatus === 'completed'
        if (bgvUploadedOrCompleted && !hasRejectedDocuments) {
          setHasSubmittedForReview(true)
        }

        // Update verification statuses
        setVerificationStatuses({
          documents: hasDocuments && !hasRejectedDocuments ? 'completed' : hasRejectedDocuments ? 'rejected' : 'pending',
          bgv: bgvStatus === 'completed' ? 'completed' : bgvStatus === 'uploaded' ? 'uploaded' : 'pending',
          adminReview: adminReview === 'approved' ? 'completed' : adminReview === 'in-review' ? 'in-review' : adminReview === 'rejected' ? 'rejected' : 'pending',
          approval: currentUser.status === 'active' ? 'completed' : 'pending'
        })

        // If admin review is approved, allow dashboard access
        if (adminReview === 'approved' && currentUser.status === 'active') {
          hasNavigatedRef.current = true
          navigate('/vendor/dashboard', { replace: true })
          return
        }

      } catch (error) {
        console.error('Error checking status:', error)
        toast.error('Error loading information')
      } finally {
        isCheckingRef.current = false
        setIsCheckingStatus(false)
        setIsLoading(false)
      }
    }

    checkStatus()
  }, [navigate, location.pathname])

  const handleFileChange = async (documentType, file) => {
    if (!file) return

    const docIndex = documents.findIndex(doc => doc.type === documentType)
    if (docIndex === -1) return

    // Check file size
    const maxSizeMB = documents[docIndex].maxSize
    if (file.size > maxSizeMB * 1024 * 1024) {
      toast.error(`File size exceeds ${maxSizeMB}MB limit`)
      return
    }

    // Check file type
    const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'application/pdf']
    if (!validTypes.includes(file.type)) {
      toast.error('Please upload PDF, JPG, or PNG files only')
      return
    }

    setUploading(true)
    setUploadProgress(prev => ({ ...prev, [documentType]: 0 }))

    try {
      const user = await vendorService.getCurrentUser()
      const userId = user?.id || user?.user_id
      const vendorId = sessionStorage.getItem('vendorId')

      if (!userId || !vendorId) {
        throw new Error('User not authenticated')
      }

      // Prepare document data for API
      const documentData = {
        file,
        document_type: documentType,
        document_name: file.name,
        document_category: 'vendor_documents',
        user_role: 'vendor',
        vendor_id: vendorId,
        description: `Vendor document: ${documents[docIndex].label}`
      }

      // Upload document
      await vendorService.uploadDocument(documentData)

      // Update document state
      const updatedDocuments = [...documents]
      updatedDocuments[docIndex] = {
        ...updatedDocuments[docIndex],
        file: file,
        preview: file.type.startsWith('image/') ? URL.createObjectURL(file) : null,
        status: 'uploaded', // Clear rejection status when re-uploading
        uploadedAt: new Date().toISOString(),
        fileName: file.name,
        fileSize: (file.size / (1024 * 1024)).toFixed(2) + ' MB',
        rejectionReason: null // Clear rejection reason when re-uploading
      }

      setDocuments(updatedDocuments)
      toast.success(`${documents[docIndex].label} uploaded successfully!`)

      // If this was a rejected document, reset hasSubmittedForReview to allow re-submission
      const wasRejected = documents[docIndex].status === 'rejected'
      if (wasRejected) {
        setHasSubmittedForReview(false)
        // Update verification status to pending since we need to re-submit
        setVerificationStatuses(prev => ({
          ...prev,
          documents: 'pending'
        }))
      } else {
        // Update verification status
        setVerificationStatuses(prev => ({
          ...prev,
          documents: 'completed'
        }))
      }

    } catch (error) {
      toast.error(error.message || `Failed to upload ${documents[docIndex].label}`)
      console.error('Upload error:', error)
    } finally {
      setUploading(false)
      setUploadProgress(prev => ({ ...prev, [documentType]: undefined }))
    }
  }

  const removeDocument = (documentType) => {
    setDocuments(prev => prev.map(doc => {
      if (doc.type === documentType) {
        if (doc.preview) {
          URL.revokeObjectURL(doc.preview)
        }
        return {
          ...doc,
          file: null,
          preview: null,
          status: 'pending',
          uploadedAt: null,
          fileName: null,
          fileSize: null
        }
      }
      return doc
    }))
    toast.success('Document removed')
  }

  const handleDragOver = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const handleDrop = (documentType, e) => {
    e.preventDefault()
    e.stopPropagation()
    const files = e.dataTransfer.files
    if (files && files[0]) {
      handleFileChange(documentType, files[0])
    }
  }

  const validateDocuments = () => {
    const requiredDocs = documents.filter(doc => doc.required)
    const missingDocs = requiredDocs.filter(doc => doc.status !== 'uploaded')
    
    if (missingDocs.length > 0) {
      toast.error(`Please upload all required documents: ${missingDocs.map(doc => doc.label).join(', ')}`)
      return false
    }
    
    return true
  }

  const handleContinue = async () => {
    if (!validateDocuments()) {
      return
    }

    // Check if there are any rejected documents that haven't been re-uploaded
    const stillRejected = documents.some(doc => doc.required && doc.status === 'rejected')
    if (stillRejected) {
      toast.error('Please re-upload all rejected documents before submitting.')
      return
    }

    try {
      setIsLoading(true)
      
      // Call API to update BGV status to 'uploaded'
      await vendorService.updateUserBGVStatus('uploaded')
      
      // Update local state
      setHasSubmittedForReview(true)
      setVerificationStatuses(prev => ({
        ...prev,
        bgv: 'uploaded',
        documents: 'completed'
      }))
      
      // Refresh user data to get updated status
      await vendorService.getCurrentUser()
      
      toast.success('Documents submitted successfully! Your documents are now under review.')
    } catch (error) {
      console.error('Error submitting documents:', error)
      toast.error(error.message || 'Error submitting documents for review. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-success-600 bg-success-50 border-success-200'
      case 'in-review': return 'text-warning-600 bg-warning-50 border-warning-200'
      case 'rejected': return 'text-error-600 bg-error-50 border-error-200'
      default: return 'text-secondary-600 bg-secondary-50 border-secondary-200'
    }
  }

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return <CheckCircle className="w-5 h-5" />
      case 'in-review': return <RefreshCw className="w-5 h-5 animate-spin" />
      case 'rejected': return <AlertCircle className="w-5 h-5" />
      default: return <FileText className="w-5 h-5" />
    }
  }

  // Check if any document is rejected
  const hasRejectedDocuments = useMemo(() => 
    documents.some(doc => doc.status === 'rejected'),
    [documents]
  )

  // Show verification screen only after user has submitted for review
  // Now show verification screen even if there are rejected documents (to allow re-upload from there)
  const allDocumentsUploaded = useMemo(() => 
    documents.filter(d => d.required).every(d => d.status === 'uploaded' || d.status === 'pending'),
    [documents]
  )
  const bgvUploaded = useMemo(() => 
    verificationStatuses.bgv === 'uploaded' || verificationStatuses.bgv === 'completed',
    [verificationStatuses.bgv]
  )
  const showVerificationScreen = useMemo(() => 
    hasSubmittedForReview && 
    allDocumentsUploaded && 
    (bgvUploaded || verificationStatuses.documents === 'completed'),
    [hasSubmittedForReview, allDocumentsUploaded, bgvUploaded, verificationStatuses.documents]
  )

  // Get rejected documents for display in verification screen
  const rejectedDocuments = useMemo(() => 
    documents.filter(doc => doc.status === 'rejected'),
    [documents]
  )

  // Ref to prevent multiple simultaneous refresh calls
  const isRefreshingRef = useRef(false)
  const refreshIntervalRef = useRef(null)

  // Periodically refresh verification status when on verification screen
  useEffect(() => {
    // Only run if we're on the documents page and verification screen is shown
    if (!showVerificationScreen || location.pathname !== '/vendor/registration/documents') {
      // Clear interval if we're not on verification screen
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current)
        refreshIntervalRef.current = null
      }
      return
    }

    const refreshStatus = async () => {
      // Prevent multiple simultaneous calls
      if (isRefreshingRef.current || hasNavigatedRef.current) {
        return
      }

      try {
        isRefreshingRef.current = true
        const currentUser = await vendorService.getCurrentUser()
        if (currentUser) {
          const adminReview = currentUser.admin_review || currentUser.adminReview
          
          // Only update state if values actually changed to prevent unnecessary re-renders
          setAdminReviewStatus(prev => prev !== adminReview ? adminReview : prev)

          setVerificationStatuses(prev => {
            const newStatuses = {
              documents: currentUser.all_documents_urls && Object.keys(currentUser.all_documents_urls).length > 0 ? 'completed' : prev.documents,
              bgv: currentUser.BGV_status === 'completed' ? 'completed' : prev.bgv,
              adminReview: adminReview === 'approved' ? 'completed' : adminReview === 'in-review' ? 'in-review' : adminReview === 'rejected' ? 'rejected' : prev.adminReview,
              approval: currentUser.status === 'active' ? 'completed' : prev.approval
            }
            
            // Only update if something changed
            if (JSON.stringify(newStatuses) === JSON.stringify(prev)) {
              return prev
            }
            return newStatuses
          })

          // If approved, allow dashboard access
          if (adminReview === 'approved' && currentUser.status === 'active') {
            hasNavigatedRef.current = true
            // Clear interval before navigating
            if (refreshIntervalRef.current) {
              clearInterval(refreshIntervalRef.current)
              refreshIntervalRef.current = null
            }
            toast.success('Your account has been verified! Redirecting to dashboard...')
            setTimeout(() => {
              navigate('/vendor/dashboard', { replace: true })
            }, 2000)
          }
        }
      } catch (error) {
        console.error('Error refreshing status:', error)
      } finally {
        isRefreshingRef.current = false
      }
    }

    // Initial refresh (with delay to avoid immediate call after mount)
    const initialTimeout = setTimeout(() => {
      refreshStatus()
    }, 1000)

    // Refresh every 30 seconds
    refreshIntervalRef.current = setInterval(refreshStatus, 30000)
    
    return () => {
      clearTimeout(initialTimeout)
      if (refreshIntervalRef.current) {
        clearInterval(refreshIntervalRef.current)
        refreshIntervalRef.current = null
      }
    }
  }, [showVerificationScreen, navigate, location.pathname])

  if (isCheckingStatus) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-secondary-900 dark:via-secondary-800 dark:to-secondary-900 flex items-center justify-center">
        <div className="text-center">
          <Loader className="w-10 h-10 mx-auto mb-4 animate-spin text-primary-600" />
          <p className="text-secondary-600 dark:text-secondary-400">Loading...</p>
        </div>
      </div>
    )
  }

  // Verification Status Screen
  if (showVerificationScreen) {
    const verificationSteps = [
      {
        id: 1,
        title: 'Document Upload & KYC',
        description: 'All required documents have been uploaded and submitted for verification',
        details: verificationStatuses.documents === 'completed' 
          ? 'All documents uploaded successfully and submitted for review'
          : verificationStatuses.documents === 'pending'
          ? 'Please upload all required documents'
          : 'Documents are being processed',
        icon: <Upload className="w-6 h-6" />,
        status: verificationStatuses.documents,
        completed: verificationStatuses.documents === 'completed',
        estimatedTime: 'Immediate'
      },
      {
        id: 2,
        title: 'Background Verification',
        description: 'Third-party partner is conducting comprehensive background verification',
        details: verificationStatuses.bgv === 'completed'
          ? 'Background verification completed successfully'
          : verificationStatuses.bgv === 'uploaded'
          ? 'Background verification is in progress by our third-party partner'
          : 'Background verification will begin after document approval',
        icon: <Shield className="w-6 h-6" />,
        status: verificationStatuses.bgv,
        completed: verificationStatuses.bgv === 'completed',
        estimatedTime: '3-5 business days'
      },
      {
        id: 3,
        title: 'Admin Review',
        description: 'Compliance team is reviewing your documents and verification results',
        details: verificationStatuses.adminReview === 'completed' || verificationStatuses.adminReview === 'approved'
          ? 'Admin review completed. Your account has been approved'
          : verificationStatuses.adminReview === 'in-review'
          ? 'Compliance team is currently reviewing your submission'
          : verificationStatuses.adminReview === 'rejected'
          ? 'Review completed with issues. Please check for rejected documents'
          : 'Admin review will begin after background verification',
        icon: <UserCheck className="w-6 h-6" />,
        status: verificationStatuses.adminReview,
        completed: verificationStatuses.adminReview === 'completed' || verificationStatuses.adminReview === 'approved',
        estimatedTime: '1-2 business days'
      }
    ]

    return (
      <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-secondary-900 dark:via-secondary-800 dark:to-secondary-900">
        <AuthHeader />
        <div className="flex flex-col items-center min-h-[calc(100vh-64px)] p-4 py-8 gap-4">
          {/* Step Indicator - Outside the white card */}
          <div className="w-full max-w-6xl">
            <StepIndicator currentStep={4} />
          </div>

          <div className="flex items-start justify-center w-full gap-4">
            {/* Sidebar on the left */}
            <RegistrationSidebar currentStep={4} isStepCompleted={isStepCompleted} />
            
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="card w-full max-w-6xl p-6 md:p-8 shadow-glow flex-1"
            >
              <div className="text-center mb-6 pb-6 border-b border-secondary-200 dark:border-secondary-700">
              <h1 className="text-3xl font-display font-bold text-secondary-900 dark:text-secondary-100 mb-2 tracking-tight">
                Verification in Progress
              </h1>
              <p className="text-sm text-secondary-600 dark:text-secondary-400 max-w-md mx-auto leading-relaxed">
                Your documents have been uploaded and are under review. Track the status of each verification step below.
              </p>
            </div>

            {/* Rejected Documents Section */}
            {hasRejectedDocuments && rejectedDocuments.length > 0 && (
              <div className="mb-6">
                <div className="bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg p-4 mb-4">
                  <div className="flex items-start gap-3">
                    <AlertCircle className="w-6 h-6 text-error-600 dark:text-error-400 mt-0.5 flex-shrink-0" />
                    <div className="flex-1">
                      <h3 className="text-sm font-semibold text-error-900 dark:text-error-100 mb-1">
                        Documents Require Re-upload
                      </h3>
                      <p className="text-xs text-error-700 dark:text-error-300">
                        {rejectedDocuments.length} document{rejectedDocuments.length > 1 ? 's have' : ' has'} been rejected. Please review the reasons and re-upload them below.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="space-y-3">
                  {rejectedDocuments.map((doc) => (
                    <div
                      key={doc.id}
                      className="border-2 border-error-200 dark:border-error-800 rounded-lg p-4 bg-error-50/50 dark:bg-error-900/10"
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-2">
                            <FileText className="w-4 h-4 text-error-600" />
                            <h4 className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">
                              {doc.label}
                            </h4>
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-error-100 text-error-700 dark:bg-error-900/30 dark:text-error-300">
                              Rejected
                            </span>
                          </div>
                          
                          {/* Rejection Reason */}
                          {doc.rejectionReason && (
                            <div className="bg-white dark:bg-secondary-800 rounded-lg p-3 mb-3 border border-error-200 dark:border-error-800">
                              <p className="text-xs font-semibold text-error-800 dark:text-error-200 mb-1">
                                Rejection Reason:
                              </p>
                              <p className="text-xs text-error-700 dark:text-error-300">
                                {doc.rejectionReason}
                              </p>
                            </div>
                          )}

                          {/* Current File Info */}
                          {doc.fileUrl && (
                            <div className="mb-3">
                              <a
                                href={doc.fileUrl}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-secondary-600 dark:text-secondary-400 hover:text-primary-600 dark:hover:text-primary-400 flex items-center gap-1"
                              >
                                <FileText className="w-3 h-3" />
                                <span>View current document</span>
                              </a>
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Re-upload Section */}
                      <div className="space-y-2">
                        {uploadProgress[doc.type] !== undefined && (
                          <div className="space-y-1">
                            <div className="w-full bg-secondary-200 dark:bg-secondary-700 rounded-full h-1.5">
                              <div
                                className="bg-primary-600 h-1.5 rounded-full transition-all"
                                style={{ width: `${uploadProgress[doc.type]}%` }}
                              />
                            </div>
                            <div className="text-xs text-secondary-500 text-center">
                              Uploading... {uploadProgress[doc.type]}%
                            </div>
                          </div>
                        )}
                        
                        <label className="block">
                          <div className="flex justify-center items-center px-4 py-3 border-2 border-error-300 dark:border-error-700 border-dashed rounded-lg hover:border-error-400 dark:hover:border-error-600 transition-colors cursor-pointer bg-white dark:bg-secondary-800">
                            <div className="space-y-1 text-center">
                              <Upload className="mx-auto h-6 w-6 text-error-600 dark:text-error-400" />
                              <div className="flex text-xs text-error-700 dark:text-error-300">
                                <span className="relative rounded-md font-medium text-error-600 hover:text-error-500">
                                  Re-upload document
                                </span>
                              </div>
                              <p className="text-[10px] text-secondary-500">
                                PDF, JPG, PNG up to {doc.maxSize}MB
                              </p>
                            </div>
                            <input
                              type="file"
                              className="hidden"
                              accept=".pdf,.jpg,.jpeg,.png"
                              onChange={(e) => {
                                if (e.target.files[0]) {
                                  handleFileChange(doc.type, e.target.files[0])
                                }
                              }}
                              disabled={uploading}
                            />
                          </div>
                        </label>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              {verificationSteps.map((step, index) => (
                <div
                  key={step.id}
                  className={`border-2 rounded-xl p-5 transition-all h-full flex flex-col ${
                    step.completed
                      ? 'border-success-200 dark:border-success-800 bg-success-50 dark:bg-success-900/20 shadow-md'
                      : step.status === 'in-review'
                      ? 'border-warning-200 dark:border-warning-800 bg-warning-50 dark:bg-warning-900/20 shadow-md'
                      : 'border-secondary-200 dark:border-secondary-700 bg-white dark:bg-secondary-800/50'
                  }`}
                >
                  <div className="flex flex-col h-full">
                    {/* Icon and Status */}
                    <div className="flex items-start justify-between mb-4">
                      <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 shadow-sm ${
                        step.completed
                          ? 'bg-success-100 dark:bg-success-900/30 text-success-600'
                          : step.status === 'in-review'
                          ? 'bg-warning-100 dark:bg-warning-900/30 text-warning-600'
                          : 'bg-secondary-100 dark:bg-secondary-700 text-secondary-400'
                      }`}>
                        {step.completed ? <CheckCircle className="w-6 h-6" /> : step.status === 'in-review' ? <RefreshCw className="w-6 h-6 animate-spin" /> : step.icon}
                      </div>
                      <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold ${getStatusColor(step.status)}`}>
                        {step.status === 'completed' || step.status === 'approved' ? 'Completed' : step.status === 'in-review' ? 'In Review' : step.status === 'rejected' ? 'Rejected' : 'Pending'}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="font-bold text-base text-secondary-900 dark:text-secondary-100 mb-2">
                      {step.title}
                    </h3>

                    {/* Description */}
                    <p className="text-xs text-secondary-600 dark:text-secondary-400 mb-3 leading-relaxed">
                      {step.description}
                    </p>

                    {/* Details */}
                    <div className="mt-auto pt-3 border-t border-secondary-200 dark:border-secondary-700">
                      <p className="text-xs text-secondary-700 dark:text-secondary-300 mb-2 leading-relaxed">
                        {step.details}
                      </p>
                      <div className="flex items-center gap-1.5 text-xs text-secondary-500 dark:text-secondary-400">
                        <Clock className="w-3 h-3" />
                        <span>Est. time: {step.estimatedTime}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {verificationStatuses.adminReview === 'approved' && !hasRejectedDocuments ? (
              <div className="bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 rounded-lg p-4 mb-6">
                <div className="flex items-center gap-3">
                  <CheckCircle className="w-6 h-6 text-success-600" />
                  <div>
                    <h3 className="font-semibold text-success-900 dark:text-success-100">
                      Verification Complete!
                    </h3>
                    <p className="text-sm text-success-700 dark:text-success-300 mt-1">
                      Your account has been verified and activated. You can now access the dashboard.
                    </p>
                  </div>
                </div>
              </div>
            ) : hasRejectedDocuments ? (
              <div className="bg-warning-50 dark:bg-warning-900/20 border border-warning-200 dark:border-warning-800 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <AlertCircle className="w-6 h-6 text-warning-600 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-warning-900 dark:text-warning-100">
                      Action Required
                    </h3>
                    <p className="text-sm text-warning-700 dark:text-warning-300 mt-1">
                      Some documents have been rejected. Please re-upload them above. Once all documents are approved, your verification will proceed.
                    </p>
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4 mb-6">
                <div className="flex items-start gap-3">
                  <Clock className="w-6 h-6 text-primary-600 mt-0.5" />
                  <div>
                    <h3 className="font-semibold text-primary-900 dark:text-primary-100">
                      Awaiting Review
                    </h3>
                    <p className="text-sm text-primary-700 dark:text-primary-300 mt-1">
                      Your documents are being reviewed by our compliance team. This process typically takes 24-48 hours. You will be notified once the review is complete.
                    </p>
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-center gap-3 pt-4 border-t border-secondary-200 dark:border-secondary-700">
              <button
                onClick={() => {
                  // Refresh status
                  window.location.reload()
                }}
                className="btn-outline h-9 text-xs font-semibold flex items-center gap-1.5 px-4"
              >
                <RefreshCw className="w-3.5 h-3.5" />
                <span>Refresh Status</span>
              </button>
              
              {/* Show Re-submit button if rejected documents were re-uploaded */}
              {hasRejectedDocuments && rejectedDocuments.length === 0 && !hasSubmittedForReview && (
                <button
                  onClick={handleContinue}
                  disabled={isLoading || uploading}
                  className="btn-primary h-9 text-xs font-semibold flex items-center gap-1.5 px-6 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <Loader className="w-3.5 h-3.5 animate-spin" />
                      <span>Submitting...</span>
                    </>
                  ) : (
                    <>
                      <span>Re-submit for Review</span>
                      <ArrowRight className="w-3.5 h-3.5" />
                    </>
                  )}
                </button>
              )}
              
              {verificationStatuses.adminReview === 'approved' && !hasRejectedDocuments && (
                <button
                  onClick={() => navigate('/vendor/dashboard')}
                  className="btn-primary h-9 text-xs font-semibold flex items-center gap-1.5 px-6"
                >
                  <span>Go to Dashboard</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </button>
              )}
            </div>
          </motion.div>
          </div>
        </div>
      </div>
    )
  }

  // Document Upload Screen
  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-accent-50 to-secondary-50 dark:from-secondary-900 dark:via-secondary-800 dark:to-secondary-900">
      <AuthHeader />
      <div className="flex flex-col items-center min-h-[calc(100vh-64px)] p-4 py-8 gap-4">
        {/* Step Indicator - Outside the white card */}
        <div className="w-full max-w-4xl">
          <StepIndicator currentStep={4} />
        </div>

        <div className="flex items-start justify-center w-full gap-4">
          {/* Sidebar on the left */}
          <RegistrationSidebar currentStep={4} />
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="card w-full max-w-4xl p-6 md:p-8 shadow-glow flex-1"
          >
            <div className="text-center mb-6 pb-6 border-b border-secondary-200 dark:border-secondary-700">
            <h1 className="text-3xl font-display font-bold text-secondary-900 dark:text-secondary-100 mb-2 tracking-tight">
              Document Upload & Verification
            </h1>
            <p className="text-sm text-secondary-600 dark:text-secondary-400 max-w-md mx-auto leading-relaxed">
              Upload all required documents for verification
            </p>
          </div>

          {/* Rejection Warning Banner */}
          {hasRejectedDocuments && (
            <div className="bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-xl p-4 mb-6">
              <div className="flex items-start">
                <AlertCircle className="w-6 h-6 text-error-600 dark:text-error-400 mt-1 flex-shrink-0" />
                <div className="ml-3">
                  <h3 className="text-sm font-semibold text-error-900 dark:text-error-100">
                    Some Documents Were Rejected
                  </h3>
                  <p className="text-xs text-error-700 dark:text-error-300 mt-1">
                    Please review the rejection reasons below and re-upload the rejected documents. Once all documents are approved, your submission will proceed to review.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Header Information */}
          <div className={`${hasRejectedDocuments ? 'bg-warning-50 dark:bg-warning-900/20 border-warning-200 dark:border-warning-800' : 'bg-primary-50 dark:bg-primary-900/20 border-primary-200 dark:border-primary-800'} border rounded-xl p-4 mb-6`}>
            <div className="flex items-start">
              <Shield className={`w-6 h-6 ${hasRejectedDocuments ? 'text-warning-600 dark:text-warning-400' : 'text-primary-600 dark:text-primary-400'} mt-1 flex-shrink-0`} />
              <div className="ml-3">
                <h3 className={`text-sm font-semibold ${hasRejectedDocuments ? 'text-warning-900 dark:text-warning-100' : 'text-primary-900 dark:text-primary-100'}`}>
                  {hasRejectedDocuments ? 'Re-upload Rejected Documents' : 'Secure Document Upload'}
                </h3>
                <p className={`text-xs ${hasRejectedDocuments ? 'text-warning-700 dark:text-warning-300' : 'text-primary-700 dark:text-primary-300'} mt-1`}>
                  {hasRejectedDocuments 
                    ? 'Please re-upload the rejected documents with the required corrections. Ensure documents are clear, legible, and meet all requirements.'
                    : 'All documents are encrypted and securely stored. Ensure documents are clear, legible, and valid.'}
                </p>
                <div className={`mt-2 text-xs ${hasRejectedDocuments ? 'text-warning-600 dark:text-warning-400' : 'text-primary-600 dark:text-primary-400'}`}>
                  <span className="font-medium">Accepted formats:</span> PDF, JPG, PNG (Max 5MB per file)
                </div>
              </div>
            </div>
          </div>

          {/* Documents Grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            {documents.map((doc) => (
              <motion.div
                key={doc.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="border-2 border-dashed border-secondary-300 dark:border-secondary-700 rounded-xl p-4 hover:border-primary-400 dark:hover:border-primary-600 transition-all"
                onDragOver={handleDragOver}
                onDrop={(e) => handleDrop(doc.type, e)}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      {getStatusIcon(doc.status)}
                      <h4 className="text-sm font-semibold text-secondary-900 dark:text-secondary-100">
                        {doc.label}
                        {doc.required && <span className="text-error-500 ml-1">*</span>}
                      </h4>
                    </div>
                    <p className="text-xs text-secondary-600 dark:text-secondary-400 mt-1">{doc.description}</p>
                    {doc.fileName && doc.status !== 'rejected' && (
                      <div className="mt-2 text-xs text-secondary-500">
                        <span className="font-medium">Uploaded:</span> {doc.fileName}
                        {doc.fileSize && ` (${doc.fileSize})`}
                      </div>
                    )}
                  </div>
                  <div className={`px-2 py-1 rounded-full text-xs font-medium border flex-shrink-0 ${
                    doc.status === 'rejected' 
                      ? 'text-error-600 bg-error-50 border-error-200 dark:text-error-400 dark:bg-error-900/20 dark:border-error-800'
                      : doc.status === 'uploaded'
                      ? 'text-success-600 bg-success-50 border-success-200 dark:text-success-400 dark:bg-success-900/20 dark:border-success-800'
                      : 'text-secondary-600 bg-secondary-50 border-secondary-200 dark:text-secondary-400 dark:bg-secondary-800 dark:border-secondary-700'
                  }`}>
                    {doc.status === 'rejected' 
                      ? 'Rejected' 
                      : doc.status === 'uploaded' 
                      ? 'Uploaded' 
                      : 'Pending'}
                  </div>
                </div>

                {doc.status === 'uploaded' || doc.status === 'rejected' ? (
                  <div className="space-y-3">
                    {/* Show file preview or link */}
                    {doc.fileUrl && (
                      <div className="relative">
                        {doc.fileUrl.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                          <img
                            src={doc.fileUrl}
                            alt="Document preview"
                            className="w-full h-32 object-contain rounded-lg border border-secondary-200 dark:border-secondary-700"
                          />
                        ) : (
                          <div className="w-full h-32 flex items-center justify-center rounded-lg border border-secondary-200 dark:border-secondary-700 bg-secondary-50 dark:bg-secondary-800">
                            <a
                              href={doc.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="flex items-center gap-2 text-primary-600 hover:text-primary-700"
                            >
                              <FileText className="w-6 h-6" />
                              <span className="text-sm font-medium">View Document</span>
                            </a>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {/* Show rejection reason if rejected */}
                    {doc.status === 'rejected' && doc.rejectionReason && (
                      <div className="bg-error-50 dark:bg-error-900/20 border border-error-200 dark:border-error-800 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="w-4 h-4 text-error-600 mt-0.5 flex-shrink-0" />
                          <div className="flex-1">
                            <p className="text-xs font-semibold text-error-800 dark:text-error-200 mb-1">
                              Rejection Reason:
                            </p>
                            <p className="text-xs text-error-700 dark:text-error-300">
                              {doc.rejectionReason}
                            </p>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Show document info */}
                    {doc.fileName && (
                      <div className="text-xs text-secondary-600 dark:text-secondary-400">
                        <span className="font-medium">File:</span> {doc.fileName}
                        {doc.fileSize && ` (${doc.fileSize})`}
                      </div>
                    )}

                    {/* Action buttons */}
                    <div className="flex gap-2">
                      {doc.status === 'rejected' ? (
                        <>
                          {/* Re-upload button for rejected documents */}
                          <label className="flex-1">
                            <div className="flex justify-center items-center px-3 py-2 border-2 border-primary-400 dark:border-primary-500 border-dashed rounded-lg hover:border-primary-500 dark:hover:border-primary-400 transition-colors cursor-pointer bg-primary-50 dark:bg-primary-900/20">
                              <div className="flex items-center gap-1.5 text-primary-600 dark:text-primary-400">
                                <Upload className="h-4 w-4" />
                                <span className="text-xs font-medium">Re-upload</span>
                              </div>
                              <input
                                type="file"
                                className="hidden"
                                accept=".pdf,.jpg,.jpeg,.png"
                                onChange={(e) => {
                                  if (e.target.files[0]) {
                                    handleFileChange(doc.type, e.target.files[0])
                                  }
                                }}
                                disabled={uploading}
                              />
                            </div>
                          </label>
                          {doc.fileUrl && (
                            <a
                              href={doc.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3 py-2 text-xs font-medium text-secondary-600 hover:text-secondary-700 hover:bg-secondary-50 dark:hover:bg-secondary-800 rounded-lg transition-colors"
                            >
                              View
                            </a>
                          )}
                        </>
                      ) : (
                        <>
                          {/* Remove button for uploaded (non-rejected) documents */}
                          <button
                            onClick={() => removeDocument(doc.type)}
                            className="flex-1 px-3 py-2 text-error-600 hover:text-error-700 hover:bg-error-50 dark:hover:bg-error-900/30 rounded-lg text-xs font-medium transition-colors"
                            disabled={uploading || hasSubmittedForReview}
                          >
                            {hasSubmittedForReview ? 'Cannot Remove' : 'Remove'}
                          </button>
                          {doc.fileUrl && (
                            <a
                              href={doc.fileUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="px-3 py-2 text-xs font-medium text-secondary-600 hover:text-secondary-700 hover:bg-secondary-50 dark:hover:bg-secondary-800 rounded-lg transition-colors"
                            >
                              View
                            </a>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {uploadProgress[doc.type] !== undefined && (
                      <div className="space-y-1">
                        <div className="w-full bg-secondary-200 dark:bg-secondary-700 rounded-full h-1.5">
                          <div
                            className="bg-primary-600 h-1.5 rounded-full transition-all"
                            style={{ width: `${uploadProgress[doc.type]}%` }}
                          />
                        </div>
                        <div className="text-xs text-secondary-500 text-center">
                          Uploading... {uploadProgress[doc.type]}%
                        </div>
                      </div>
                    )}
                    <label className="block">
                      <div className="flex justify-center px-4 pt-4 pb-4 border-2 border-secondary-300 dark:border-secondary-600 border-dashed rounded-lg hover:border-primary-400 dark:hover:border-primary-500 transition-colors cursor-pointer">
                        <div className="space-y-1 text-center">
                          <Upload className="mx-auto h-8 w-8 text-secondary-400" />
                          <div className="flex text-xs text-secondary-600 dark:text-secondary-400">
                            <span className="relative rounded-md font-medium text-primary-600 hover:text-primary-500">
                              Upload a file
                            </span>
                            <p className="pl-1">or drag and drop</p>
                          </div>
                          <p className="text-[10px] text-secondary-500">
                            PDF, JPG, PNG up to {doc.maxSize}MB
                          </p>
                        </div>
                        <input
                          type="file"
                          className="hidden"
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => {
                            if (e.target.files[0]) {
                              handleFileChange(doc.type, e.target.files[0])
                            }
                          }}
                          disabled={uploading}
                        />
                      </div>
                    </label>
                  </div>
                )}
              </motion.div>
            ))}
          </div>

          {/* Summary */}
          <div className="bg-secondary-50 dark:bg-secondary-800/50 rounded-xl p-4 mb-6">
            <h4 className="text-sm font-semibold text-secondary-900 dark:text-secondary-100 mb-3">Document Summary</h4>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              <div className="text-center p-3 bg-white dark:bg-secondary-800 rounded-lg">
                <div className="text-xl font-bold text-secondary-900 dark:text-secondary-100">
                  {documents.filter(d => d.required).length}
                </div>
                <div className="text-xs text-secondary-600 dark:text-secondary-400">Required</div>
              </div>
              <div className="text-center p-3 bg-white dark:bg-secondary-800 rounded-lg">
                <div className="text-xl font-bold text-success-600">
                  {documents.filter(d => d.status === 'uploaded').length}
                </div>
                <div className="text-xs text-secondary-600 dark:text-secondary-400">Uploaded</div>
              </div>
              <div className="text-center p-3 bg-white dark:bg-secondary-800 rounded-lg">
                <div className="text-xl font-bold text-secondary-900 dark:text-secondary-100">
                  {documents.filter(d => !d.required).length}
                </div>
                <div className="text-xs text-secondary-600 dark:text-secondary-400">Optional</div>
              </div>
              <div className="text-center p-3 bg-white dark:bg-secondary-800 rounded-lg">
                <div className="text-xl font-bold text-warning-600">
                  {documents.filter(d => d.required && d.status !== 'uploaded').length}
                </div>
                <div className="text-xs text-secondary-600 dark:text-secondary-400">Pending</div>
              </div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between pt-4 border-t border-secondary-200 dark:border-secondary-700">
            <button
              onClick={() => navigate('/vendor/registration/subscription')}
              disabled={isLoading || uploading}
              className="btn-outline h-9 text-xs font-semibold flex items-center gap-1.5 px-4"
            >
              <ArrowLeft className="w-3.5 h-3.5" />
              <span>Back</span>
            </button>
            <button
              onClick={handleContinue}
              disabled={isLoading || uploading || !allDocumentsUploaded || (hasSubmittedForReview && !hasRejectedDocuments)}
              className="btn-primary h-9 text-xs font-semibold flex items-center gap-1.5 px-6 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isLoading ? (
                <>
                  <Loader className="w-3.5 h-3.5 animate-spin" />
                  <span>Submitting...</span>
                </>
              ) : hasSubmittedForReview && !hasRejectedDocuments ? (
                <>
                  <CheckCircle className="w-3.5 h-3.5" />
                  <span>Submitted</span>
                </>
              ) : hasRejectedDocuments ? (
                <>
                  <span>Re-submit for Review</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </>
              ) : (
                <>
                  <span>Submit for Review</span>
                  <ArrowRight className="w-3.5 h-3.5" />
                </>
              )}
            </button>
          </div>
        </motion.div>
        </div>
      </div>
    </div>
  )
}

export default DocumentUploadPage

